import React, { useEffect } from 'react';
import './index.css';
import './output.css';
import ReactDom from 'react-dom';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

// Import Theme Provider
import { ThemeProvider } from './contexts/ThemeContext';

// Main Components
import Layout from './Layout';
import Home from './component/home';
import Login from './component/login';
import ProductOverview from './component/ProductsOverview';
import Wishlist from './component/Wishlist';
import Cart from './component/cart';
import Checkout from './component/checkout';
import Error from './component/Error';
import ForgotPassword from './component/ForgetPassword';
import ProtectedRoute from './protectedRoutes/ProtectedRoute';

// Product View Pages
import FridgeTopCover from './ProductViewPage/FridgeTopCoverView';
import TableCover from './ProductViewPage/TableCover';
import KitchenApron from './ProductViewPage/KitchenApronView';
import WashingMachineView from './ProductViewPage/WashingMachineCoverView';
import Products from './ProductViewPage/Products';
import AllProducts from './ProductViewPage/AllProducts';

// User Dashboard
import Receipt from './userDashbord/Receipt';
import UserSidebar from './userDashbord/UserSidebar';
import ViewDetails from './userDashbord/ViewDetails';
import OrderSection from './userDashbord/OrderSection';
import ProfileSection from './userDashbord/UserProfile';

// Public Pages
import AboutUs from './component/AboutUs';
import Teams from './component/Teams';
import Contact from './component/Contact';
import Gallery from './component/Gallery';
import TradeFairParticipation from './component/TradeFairParticipation';

import TermsAndConditions from './component/TermsAndCondition';
import PrivacyPolicy from './component/PrivacyPolicy';
import ReturnExchangePolicy from './component/Return';

import Under1000Products from './component/Under1000Products';
import Deals from './component/Deals';

// ======================================================
// BLOGS
// ======================================================

// Main page containing all blogs
import Blogs from './component/BlogsPage';

// Individual blog details page
import BlogDetail from './component/BlogDetail';

// ======================================================
// ADMIN COMPONENTS
// ======================================================

import DashboardMain from './admin/DashboardMain';
import AdminDashboard from './admin/Dashboard/Dashbord';

import AddProduct from './admin/Ecommerce/addProduct';
import ProductList from './admin/Ecommerce/productList';
import ManageDiscount from './admin/Ecommerce/ManageDiscounts';
import UpdateProduct from './admin/Ecommerce/updateProduct';
import StockManagement from './admin/Ecommerce/StockManagement';

import DetailedReport from './admin/Reporting/DetailedReport';
import OrderSummary from './admin/Reporting/OrderSummary';
import ProductReport from './admin/Reporting/ProductReport';
import RecentOrders from './admin/Reporting/RecentOrders';

import AddCategory from './admin/Category/addCategory';
import CategoryList from './admin/Category/categoryList';
import AddProductSize from './admin/Category/AddProductSize';
import HSNCode from './admin/Category/AddHsnCode';
import AdminPurities from './admin/Category/AdminPurities';

import UserList from './admin/User/userList';

import CompletedOrders from './admin/Dashboard/CompletedOrders';
import AdminViewDetails from './admin/Dashboard/AdminViewDetails';
import AdminReceipt from './admin/Dashboard/AdminReceipt';

// Admin Banner / Content Management
import BannerAdmin from './admin/banner/AdminHomeBanner';
import AdminHeroSlides from './admin/banner/AdminHeroSlides';
import AdminHeroSettings from './admin/banner/AdminHeroSettings';
import AdminMarquee from './admin/banner/AdminMarquee';
import AdminWhyKanthi from './admin/banner/AdminWhyKanthi';
import AdminHomeHero from './admin/banner/AdminHomeHero';

import AdminBlogs from './admin/banner/AdminBlogs';

import AdminAboutPage from './admin/banner/AdminAboutPage';
import AdminTeams from './admin/banner/AdminTeams';
import AdminContactMessages from './admin/banner/AdminContactMessages';
import AdminAwards from './admin/banner/AdminAwards';
import AdminTradeFairs from './admin/banner/AdminTradeFairs';

import PriceSettings from './admin/banner/AdminPriceSetting';
import AdminCoupons from './admin/banner/AdminCoupon';

// Theme Settings
import ThemeColorSettings from './admin/banner/ThemeColorSettings';


// ======================================================
// SCROLL TO TOP
// ======================================================

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [pathname, search]);

  return null;
};


// ======================================================
// APP
// ======================================================

const App = () => {
  return (
    <Router>
      <ThemeProvider>

        <ScrollToTop />

        <Routes>

          {/* ==================================================
              MAIN WEBSITE
          ================================================== */}

          <Route path="/" element={<Layout />}>

            {/* HOME */}
            <Route index element={<Home />} />


            {/* ==================================================
                PRODUCTS
            ================================================== */}

            <Route
              path="table-top"
              element={<TableCover />}
            />

            <Route
              path="kitchen-apron"
              element={<KitchenApron />}
            />

            <Route
              path="fridge-top-cover"
              element={<FridgeTopCover />}
            />

            <Route
              path="washing-machine-cover"
              element={<WashingMachineView />}
            />

            <Route
              path="ProductOverview/:id"
              element={<ProductOverview />}
            />

            <Route
              path="products/:id"
              element={<Products />}
            />

            <Route
              path="all-products"
              element={<AllProducts />}
            />

            <Route
              path="under-price"
              element={<Under1000Products />}
            />

            <Route
              path="deals"
              element={<Deals />}
            />


            {/* ==================================================
                ACCOUNT
            ================================================== */}

            <Route
              path="login"
              element={<Login />}
            />

            <Route
              path="forgetPassword"
              element={<ForgotPassword />}
            />

            <Route
              path="wishlist"
              element={<Wishlist />}
            />


            {/* ==================================================
                COMPANY PAGES
            ================================================== */}

            <Route
              path="about-us"
              element={<AboutUs />}
            />

            <Route
              path="teams"
              element={<Teams />}
            />

            <Route
              path="our-team"
              element={<Teams />}
            />

            <Route
              path="contact"
              element={<Contact />}
            />

            <Route
              path="gallery"
              element={<Gallery />}
            />

            <Route
              path="trade-fare-participation"
              element={<TradeFairParticipation />}
            />


            {/* ==================================================
                BLOGS
            ================================================== */}

            {/* 
              MAIN BLOG PAGE

              URL:
              http://localhost:3000/blogs
            */}
            <Route
              path="blogs"
              element={<Blogs />}
            />


            {/* 
              INDIVIDUAL BLOG DETAILS

              Examples:

              /blog/1
              /blog/2
              /blog/15

              BlogDetail receives the ID using:

              const { id } = useParams();
            */}
            <Route
              path="blog/:id"
              element={<BlogDetail />}
            />


            {/* ==================================================
                POLICIES
            ================================================== */}

            <Route
              path="terms-and-condition"
              element={<TermsAndConditions />}
            />

            <Route
              path="privacy-policy"
              element={<PrivacyPolicy />}
            />

            <Route
              path="return"
              element={<ReturnExchangePolicy />}
            />


            {/* ==================================================
                USER PROTECTED ROUTES
            ================================================== */}

            <Route
              path="cart"
              element={
                <ProtectedRoute element={Cart} />
              }
            />

            <Route
              path="cart/checkout"
              element={
                <ProtectedRoute element={Checkout} />
              }
            />

            <Route
              path="order-section"
              element={
                <ProtectedRoute element={OrderSection} />
              }
            />

            <Route
              path="profile"
              element={
                <ProtectedRoute element={ProfileSection} />
              }
            />

            <Route
              path="Receipt/:order_id"
              element={
                <ProtectedRoute element={Receipt} />
              }
            />

            <Route
              path="dashboard"
              element={
                <ProtectedRoute element={UserSidebar} />
              }
            />

            <Route
              path="ViewDetails/:order_id"
              element={<ViewDetails />}
            />


            {/* ==================================================
                PUBLIC 404
            ================================================== */}

            <Route
              path="*"
              element={<Error />}
            />

          </Route>


          {/* ==================================================
              ADMIN
          ================================================== */}

          <Route
            path="/admin"
            element={
              <ProtectedRoute
                element={DashboardMain}
                adminOnly={true}
              />
            }
          >

            <Route
              index
              element={
                <Navigate
                  to="dashboard"
                  replace
                />
              }
            />


            {/* Dashboard */}

            <Route
              path="dashboard"
              element={<AdminDashboard />}
            />

            <Route
              path="orders"
              element={<CompletedOrders />}
            />


            {/* ==================================================
                ADMIN WEBSITE CONTENT
            ================================================== */}

            <Route
              path="banner"
              element={<BannerAdmin />}
            />

            <Route
              path="banner/hero"
              element={<AdminHeroSlides />}
            />

            <Route
              path="banner/text"
              element={<AdminHeroSettings />}
            />

            <Route
              path="banner/home-hero"
              element={<AdminHomeHero />}
            />

            <Route
              path="banner/why-kanthi"
              element={<AdminWhyKanthi />}
            />


            {/* ==================================================
                ADMIN BLOGS
            ================================================== */}

            <Route
              path="banner/blogs"
              element={<AdminBlogs />}
            />


            <Route
              path="banner/about-us"
              element={<AdminAboutPage />}
            />

            <Route
              path="banner/teams"
              element={<AdminTeams />}
            />

            <Route
              path="banner/contact-messages"
              element={<AdminContactMessages />}
            />

            <Route
              path="banner/awards"
              element={<AdminAwards />}
            />

            <Route
              path="banner/trade-fairs"
              element={<AdminTradeFairs />}
            />

            <Route
              path="banner/marquee"
              element={<AdminMarquee />}
            />

            <Route
              path="banner/under"
              element={<PriceSettings />}
            />

            <Route
              path="banner/coupon"
              element={<AdminCoupons />}
            />


            {/* ==================================================
                THEME
            ================================================== */}

            <Route
              path="theme-colors"
              element={<ThemeColorSettings />}
            />


            {/* ==================================================
                ECOMMERCE
            ================================================== */}

            <Route
              path="add-product"
              element={<AddProduct />}
            />

            <Route
              path="product-list"
              element={<ProductList />}
            />

            <Route
              path="manage-discount"
              element={<ManageDiscount />}
            />

            <Route
              path="update-product"
              element={<UpdateProduct />}
            />

            <Route
              path="stock-management"
              element={<StockManagement />}
            />


            {/* ==================================================
                REPORTING
            ================================================== */}

            <Route
              path="detailed-report"
              element={<DetailedReport />}
            />

            <Route
              path="order-summary"
              element={<OrderSummary />}
            />

            <Route
              path="product-report"
              element={<ProductReport />}
            />

            <Route
              path="recent-orders"
              element={<RecentOrders />}
            />


            {/* ==================================================
                CATEGORIES
            ================================================== */}

            <Route
              path="add-category"
              element={<AddCategory />}
            />

            <Route
              path="category-list"
              element={<CategoryList />}
            />

            <Route
              path="add-product-size"
              element={<AddProductSize />}
            />

            <Route
              path="hsn-code"
              element={<HSNCode />}
            />

            <Route
              path="purities"
              element={<AdminPurities />}
            />


            {/* ==================================================
                USERS
            ================================================== */}

            <Route
              path="user-list"
              element={<UserList />}
            />


            {/* ==================================================
                ADMIN ORDER DETAILS
            ================================================== */}

            <Route
              path="AdminViewDetails/:order_id"
              element={<AdminViewDetails />}
            />

            <Route
              path="AdminReceipt/:order_id"
              element={<AdminReceipt />}
            />


            {/* ADMIN 404 */}

            <Route
              path="*"
              element={
                <Navigate
                  to="dashboard"
                  replace
                />
              }
            />

          </Route>


          {/* ==================================================
              GLOBAL FALLBACK
          ================================================== */}

          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />

        </Routes>

      </ThemeProvider>
    </Router>
  );
};


// ======================================================
// RENDER APP
// ======================================================

ReactDom.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
);